import React, {useCallback, useEffect, useState} from 'react';

import {TREE_NAMES} from 'src/components/features-section';
import {useAlertFrequencyTypes} from 'src/hooks/dictionaries/useAlertDictionaries';
import {
    ALERT_TYPE_MAPPING,
    ERROR_MESSAGES,
    descriptionMap,
    getTreeData,
} from 'src/modules/profile/user/tabs/content/utils/util';
import {
    getSavedListAvailableOptions,
    getSavedSearchAvailableOptions,
} from 'src/openapi/admin/apis/AlertAvailableOptionsApi';
import {getSavedContentGeneralAlert} from 'src/openapi/admin/apis/SavedContentAlertApi';
import {AlertAvailableOption} from 'src/openapi/admin/models/AlertAvailableOption';
import {AlertFrequencyType} from 'src/openapi/admin/models/AlertFrequencyType';
import {GeneralAlertType} from 'src/openapi/admin/models/GeneralAlertType';
import {SavedListType} from 'src/openapi/admin/models/SavedListType';
import {useAppDispatch} from 'src/store/hooks';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import CheckBoxTree, {ChangeTreeType} from '@pb/kit-component-checkbox-tree';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group, Item} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {RadioButton, RadioButtons} from '@pb/kit-component-radio-button';

import {alertDescription} from './alert-modal.module.styl';

import type {AdvancedSearchType} from 'src/openapi/admin/models/AdvancedSearchType';
import type {ContentAlert} from 'src/openapi/admin/models/ContentAlert';

const UI_LABELS = {
    MODAL_CAPTION: (title: string) => `Email Alert Settings for ${title}`,
    RECEIVE_EMAIL_ALERTS: 'Receive Email Alerts:',
    ALERTS_CONTAIN: 'Alerts Contain:',
    WARNING_MESSAGE: 'You cannot change alert settings. Only the view mode is available',
    OK_BUTTON: 'Close',
    NONE_OPTION: 'None',
    ALERT_FREQUENCY_ARIA_LABEL: 'Alert frequency options',
} as const;

type Props = {
    contentId: string,
    title: string,
    alertTypeCode: string,
    isOpen: boolean,
    onClose: () => void,
    userId: number,
};

export const AlertDetailsModal = (props: Props) => {
    const {contentId, isOpen, onClose, userId, alertTypeCode, title} = props;
    const apiDispatch = useAppDispatch();

    const [contentAlert, setContentAlert] = useState<ContentAlert | null>(null);
    const [alertOptions, setAlertOptions] = useState<AlertAvailableOption | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedFrequency, setSelectedFrequency] = useState<AlertFrequencyType>(AlertFrequencyType.NONE);
    const [selectedChangeTypes, setSelectedChangeTypes] = useState<Array<string>>([]);

    const handleChangeTypesChange = useCallback((checked: ChangeTreeType) => {
        setSelectedChangeTypes(checked.checkedIdsList);
    }, []);

    const {getAlertFrequencyTypeByCode} = useAlertFrequencyTypes();

    const loadContent = useCallback(async (id: string) => {
        setLoading(true);
        try {
            const alertResponse = await apiDispatch(getSavedContentGeneralAlert({requestBody: [id]}));
            if (alertResponse.payload.success) {
                const content = alertResponse.payload.data[0];
                setContentAlert(content);
                setSelectedFrequency(content.alertFrequency);
                setSelectedChangeTypes(content.changeTypes);

                if (content.generalAlertType === GeneralAlertType.SAVED_SEARCH) {
                    const mappedType =
                        ALERT_TYPE_MAPPING[alertTypeCode as keyof typeof ALERT_TYPE_MAPPING] || alertTypeCode;
                    const response = await apiDispatch(getSavedSearchAvailableOptions({
                        userId,
                        advancedSearchType: mappedType as AdvancedSearchType,
                    }));
                    if (response.payload.success) {
                        setAlertOptions(response.payload.data);
                    } else {
                        setError(ERROR_MESSAGES.LOAD_SEARCH_OPTIONS);
                    }
                } else if (content.generalAlertType === GeneralAlertType.SAVED_LIST) {
                    const response = await apiDispatch(getSavedListAvailableOptions({
                        userId,
                        savedListType: alertTypeCode as SavedListType,
                    }));
                    if (response.payload.success) {
                        setAlertOptions(response.payload.data);
                    } else {
                        setError(ERROR_MESSAGES.LOAD_LIST_OPTIONS);
                    }
                }
            } else {
                setError(ERROR_MESSAGES.LOAD_CONTENT_ALERT);
            }
        } catch {
            setError(ERROR_MESSAGES.LOAD_CONTENT_ALERT);
        } finally {
            setLoading(false);
        }
    }, [apiDispatch, alertTypeCode, userId]);

    useEffect(() => {
        loadContent(contentId);
    }, [loadContent, contentId]);

    const handleFrequencyChange = useCallback((value: AlertFrequencyType) => {
        setSelectedFrequency(value);
    }, []);

    if (!contentAlert || !alertOptions) {
        return null;
    }

    return (
        <Dialog
            shown={isOpen}
            setShown={onClose}>
            <DialogHeader onClose={onClose}>
                {UI_LABELS.MODAL_CAPTION(title)}
            </DialogHeader>
            <DialogMain>
                {error
                    ? <Message color="error">{error}</Message>
                    : <Message color="warning">{UI_LABELS.WARNING_MESSAGE}</Message>}
                <BoxLoading loading={loading}>
                    <Group direction="v" gap="xl" style={{padding: '2rem'}}>
                        <Group direction="h">
                            <Item style={{width: 250}} className="pdx-xs">
                                <Label>{UI_LABELS.RECEIVE_EMAIL_ALERTS}</Label>
                                <RadioButtons
                                    name="alert-frequency"
                                    value={selectedFrequency}
                                    onChange={handleFrequencyChange}
                                    disabled={true}
                                    grouping={{'aria-label': UI_LABELS.ALERT_FREQUENCY_ARIA_LABEL}}>
                                    {Object.values(AlertFrequencyType)
                                        .filter(code => code !== AlertFrequencyType.NONE)
                                        .map(code => {
                                            const baseLab = getAlertFrequencyTypeByCode(code)?.description || code;
                                            const selected = selectedFrequency === code;
                                            const desc = selected
                                                ? descriptionMap[code as AlertFrequencyType]
                                                : undefined;
                                            return (
                                                <RadioButton
                                                    key={code}
                                                    value={code}
                                                    startAdornment={<Icon type={Icons.BELL} size="Size_14"/>}
                                                    label={(
                                                        <div>
                                                            <div>{baseLab}</div>
                                                            {desc ? (
                                                                <div className={alertDescription}>
                                                                    {desc}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                />
                                            );
                                        })}
                                    <RadioButton
                                        key={UI_LABELS.NONE_OPTION}
                                        value={AlertFrequencyType.NONE}
                                        startAdornment={<Icon type={Icons.BELL_LIGHT} size="Size_14"/>}
                                        label={UI_LABELS.NONE_OPTION}
                                    />
                                </RadioButtons>
                            </Item>
                            <Item className="group-item pdx-xs">
                                <Label>{UI_LABELS.ALERTS_CONTAIN}</Label>
                                <CheckBoxTree
                                    data={
                                        getTreeData(
                                            alertOptions.availableTree,
                                            true,
                                        )
                                    }
                                    onChange={handleChangeTypesChange}
                                    name={TREE_NAMES.EXPORT_OPTIONS}
                                    checkedIdsList={selectedChangeTypes}
                                />
                            </Item>
                        </Group>
                    </Group>
                </BoxLoading>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button variant="Type.PRIMARY" onClick={onClose}>{UI_LABELS.OK_BUTTON}</Button>
                </Group>
            </DialogFooter>
        </Dialog>
    );
};
